import { useEffect, useState } from "react";
import { api, fmtDateTime } from "@/lib/api";
import { ShieldCheck, MagnifyingGlass } from "@phosphor-icons/react";

export default function AuditLog() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const load = async () => {
    const params = { limit, offset };
    if (q) params.q = q;
    if (action) params.action = action;
    const { data } = await api.get("/audit-log", { params });
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [offset, action]);

  return (
    <div className="space-y-5" data-testid="audit-page">
      <div>
        <div className="label-eyebrow flex items-center gap-1.5"><ShieldCheck size={11}/> Compliance</div>
        <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Audit log · {data.total}</h1>
        <div className="text-[12px] text-[var(--span-text-2)] mt-1">Every state change is recorded with actor, resource, timestamp.</div>
      </div>

      <div className="card-flat p-4 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px] hairline flex items-center gap-2 px-3 h-9 surface">
          <MagnifyingGlass size={14} className="text-[var(--span-text-2)]"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (setOffset(0), load())} placeholder="Filter by resource or user email…" className="bg-transparent text-[13px] outline-none w-full" data-testid="audit-search"/>
        </div>
        <select value={action} onChange={(e) => { setAction(e.target.value); setOffset(0); }} className="span-input max-w-[220px] h-9 py-0" data-testid="audit-action-filter">
          <option value="">All actions</option>
          {["billing.checkout.start", "billing.subscription.activated", "comment.create", "intel.llm_scan", "api_key.create", "api_key.revoke", "export.cases", "export.invoices", "export.payables", "organization.create"].map(a => <option key={a}>{a}</option>)}
        </select>
        <button onClick={() => { setOffset(0); load(); }} className="span-btn span-btn-ghost h-9">Apply</button>
      </div>

      <div className="card-flat overflow-hidden">
        <table className="span-table">
          <thead><tr><th>Timestamp</th><th>Actor</th><th>Action</th><th>Resource</th><th>Meta</th></tr></thead>
          <tbody>
            {data.items.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-[12px] text-[var(--span-text-2)]">No audit entries.</td></tr>}
            {data.items.map(a => (
              <tr key={a.audit_id} data-testid={`audit-row`}>
                <td className="font-mono text-[11px]">{fmtDateTime(a.timestamp)}</td>
                <td>
                  <div className="text-[12px]">{a.user_name || "—"}</div>
                  <div className="text-[10px] font-mono text-[var(--span-text-2)]">{a.user_email || "system"}</div>
                </td>
                <td><span className="badge font-mono">{a.action}</span></td>
                <td className="text-[12px] text-[var(--span-text-2)]">{a.resource}{a.resource_id ? ` · ${a.resource_id.slice(0, 14)}…` : ""}</td>
                <td className="font-mono text-[10px] text-[var(--span-text-2)]">{a.meta && Object.keys(a.meta).length > 0 ? JSON.stringify(a.meta).slice(0, 120) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-[11px] font-mono text-[var(--span-text-2)]">Showing {offset + 1}–{Math.min(offset + data.items.length, data.total)} of {data.total}</span>
        <div className="flex gap-1">
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} className="span-btn span-btn-ghost h-8 text-[11px] disabled:opacity-40">Prev</button>
          <button disabled={offset + limit >= data.total} onClick={() => setOffset(offset + limit)} className="span-btn span-btn-ghost h-8 text-[11px] disabled:opacity-40">Next</button>
        </div>
      </div>
    </div>
  );
}
